import { Box, CloseButton, Group, Paper, Progress, Text, Button, useMantineTheme } from '@mantine/core';
import { IconCheck, IconX, IconInfoCircle, IconAlertTriangle } from '@tabler/icons-react';
import { forwardRef, useEffect, useState } from 'react';
import { ToastAlert } from '../providers/alert-types';
import { useReducedMotion } from '../../animations/hooks';
import { pickTextColorForBackground } from '../../../utils/color';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface ToastItemProps {
  toast: ToastAlert;
  onDismiss: () => void;
}

const ToastItem = forwardRef<HTMLDivElement, ToastItemProps>(({ toast, onDismiss }, ref) => {
  const theme = useMantineTheme();
  const reducedMotion = useReducedMotion();
  const [progress, setProgress] = useState(100);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Calculate background and text color first
  const gradient = toast.gradient && toast.gradientVariant ? theme.other.gradients[toast.gradientVariant] : null;
  const backgroundValue = gradient
    ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
    : 'transparent';
  const textColor = gradient ? pickTextColorForBackground(backgroundValue) : undefined;

  const typeConfig = {
    success: {
      color: theme.colors.green[6],
      icon: <IconCheck size={20} style={{ color: gradient ? textColor : undefined }} />
    },
    error: {
      color: theme.colors.red[6],
      icon: <IconX size={20} style={{ color: gradient ? textColor : undefined }} />
    },
    warning: {
      color: theme.colors.yellow[6],
      icon: <IconAlertTriangle size={20} style={{ color: gradient ? textColor : undefined }} />
    },
    info: {
      color: theme.colors.blue[6],
      icon: <IconInfoCircle size={20} style={{ color: gradient ? textColor : undefined }} />
    },
  };

  const config = typeConfig[toast.type];

  // Progress bar animation
  useEffect(() => {
    const duration = toast.duration ?? 0;
    if (duration <= 0) return;

    const startTime = Date.now();
    const endTime = startTime + duration;

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = endTime - now;
      const percent = (remaining / duration) * 100;

      if (percent <= 0) {
        setProgress(0);
        clearInterval(interval);
      } else {
        setProgress(percent);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [toast.duration]);

  return (
    <Paper
      ref={ref}
      shadow="md"
      p="md"
      radius="md"
      style={{
        minWidth: 300,
        maxWidth: 400,
        position: 'relative',
        overflow: 'hidden',
        ...accessibilityStyles,
        ...(gradient
          ? {
              backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              color: textColor,
            }
          : {}),
      }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={toast.action ? 'xs' : 0}>
        <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
          <Box
            style={{
              color: gradient ? textColor : config.color,
              flexShrink: 0,
            }}
          >
            {toast.icon || config.icon}
          </Box>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} size="sm" style={{ wordBreak: 'break-word' }}>
              {toast.title}
            </Text>
            {toast.message && (
              <Text size="xs" opacity={0.8} mt={4} style={{ wordBreak: 'break-word' }}>
                {toast.message}
              </Text>
            )}
          </Box>
        </Group>
        <CloseButton
          size="sm"
          onClick={() => {
            toast.onClose?.();
            onDismiss();
          }}
          style={{ color: gradient ? textColor : undefined, flexShrink: 0 }}
        />
      </Group>

      {/* Action Button */}
      {toast.action && (
        <Box mt="sm">
          <Button
            variant="white"
            color="dark"
            size="xs"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
            fullWidth
          >
            {toast.action.label}
          </Button>
        </Box>
      )}

      {/* Progress Bar */}
      {toast.duration && toast.duration > 0 && (
        <Box
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          <Progress
            value={progress}
            size="xs"
            color={gradient ? textColor : config.color}
            styles={{
              root: {
                backgroundColor: gradient ? 'rgba(255, 255, 255, 0.2)' : undefined,
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
});

ToastItem.displayName = 'ToastItem';

export default ToastItem;
